import { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, ChevronDown, ChevronUp, X, CheckCircle, ArrowLeft, Clock, ChevronLeft, ChevronRight, AlertTriangle, History, Download, Image as ImageIcon, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fmt, fmtCur, fmtWt, procTotal, bopTotal, netRmc, addOn, CostCell, RmVarianceBadge, MarginBadge, PlatingSupplierBubble, isNegativePart } from './helpers';
import { PartNoteButton } from './PartNote';
import { CostBreakdownModal } from './CostBreakdownModal';
import { CostingHistoryModal } from './CostingHistoryModal';
import { RevisionHistoryModal } from './RevisionHistoryModal';
import { RMRevisionPanel } from './RMRevisionPanel';
import { PartModal } from './PartModal';
import { PartImagesViewerModal } from './PartImagesViewerModal';
import MarginCompareView from '../../components/MarginCompareView';

const PAGE_SIZE = 50;

export default function Parts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFromUrl = searchParams.get('customer') || '';
  const marginRiskFilter = searchParams.get('margin_risk') === 'true';
  const [parts, setParts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [platingMap, setPlatingMap] = useState(null);
  const [totalParts, setTotalParts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCustomer, setFilterCustomer] = useState(customerFromUrl);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [sortField, setSortField] = useState('margin_pct');
  const [sortDir, setSortDir] = useState('asc');
  const [editPart, setEditPart] = useState(null);
  const [viewImagesPart, setViewImagesPart] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [costingPart, setCostingPart] = useState(null);
  const [historyPart, setHistoryPart] = useState(null);
  const [revHistoryPart, setRevHistoryPart] = useState(null);
  const [showRMRevision, setShowRMRevision] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filterRevision, setFilterRevision] = useState('');
  const [filterRmStatus, setFilterRmStatus] = useState('');
  const [allRevisions, setAllRevisions] = useState([]);
  const [exporting, setExporting] = useState(false);

  const isCustomerView = !!customerFromUrl;
  const isMarginRiskView = marginRiskFilter;
  const canEdit = user?.role === 'admin';
  // Editing a customer part is capability-gated: admin, or any role granted EDIT_PART_COSTING
  // (e.g. Costing Editor). RM/scrap/selling stay locked in the sheet; Delete/bulk remain admin-only.
  const canEditParts = canEdit || (user?.capabilities || []).includes('edit_part_costing');
  // Roles that may override the selling price but not full costing (e.g. Accounts Head).

  const activeTab = searchParams.get('tab') === 'margin-compare' && user?.role === 'admin'
    ? 'margin-compare'
    : 'all-parts';

  const setTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'all-parts') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next);
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const c = searchParams.get('customer') || '';
    setFilterCustomer(c);
  }, [searchParams]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, filterCustomer, filterCategory, filterGrade, filterRevision, filterRmStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', currentPage);
      params.set('limit', PAGE_SIZE);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterCustomer) params.set('customer', filterCustomer);
      if (filterCategory) params.set('category', filterCategory);
      if (filterGrade) params.set('grade', filterGrade);
      if (filterRevision) params.set('revision', filterRevision);
      if (filterRmStatus) params.set('rm_status', filterRmStatus);
      if (marginRiskFilter) params.set('margin_risk', 'true');

      const [partsRes, matsRes] = await Promise.all([
        api.get(`/parts?${params.toString()}`),
        api.get('/materials')
      ]);

      const data = partsRes.data;
      if (data.parts) {
        setParts(data.parts);
        setTotalParts(data.total);
        setTotalPages(data.pages);
        setSummary(data.summary || null);
      } else {
        setParts(data);
        setTotalParts(data.length);
        setTotalPages(1);
        setSummary(null);
      }
      setMaterials(matsRes.data);

      // Load filter options once
      if (allCustomers.length === 0) {
        try {
          const [custRes, revRes] = await Promise.all([
            api.get('/customers'),
            api.get('/upload/revisions'),
          ]);
          setAllCustomers(custRes.data.map(c => c.name).sort());
          setAllCategories([...new Set(matsRes.data.map(m => m.category))].sort());
          setAllRevisions(revRes.data);
        } catch {}
      }
    } catch {
      toast.error('Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [currentPage, debouncedSearch, filterCustomer, filterCategory, filterGrade, filterRevision, filterRmStatus, marginRiskFilter]);

  // Best supplier plating cost per part, for the plating balloons (needs plating access).
  useEffect(() => {
    if (user?.role !== 'admin' && !(user?.capabilities || []).includes('access_plating')) return;
    api.get('/plating-costings/summary').then(r => setPlatingMap(r.data || {})).catch(() => {});
  }, [user]);

  const customers = allCustomers.length > 0 ? allCustomers : [...new Set(parts.map(p => p.customer_name))].sort();
  const categories = allCategories.length > 0 ? allCategories : [...new Set(parts.map(p => p.material_category))].sort();

  const filtered = useMemo(() => {
    let result = [...parts];
    result.sort((a, b) => {
      if (sortField === 'rm_status') {
        // Behind (0) < Breakeven (1) < Safe (2) < no data (3), so ascending puts Behind on top.
        const rank = p => p.rm_variance ? ({ behind: 0, breakeven: 1, safe: 2 }[p.rm_variance.status] ?? 3) : 3;
        const d = rank(a) - rank(b);
        return sortDir === 'asc' ? d : -d;
      }
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return result;
  }, [parts, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const applyNote = (partId, note, by, at) =>
    setParts(ps => ps.map(p => p.part_id === partId ? { ...p, note, note_updated_by: by, note_updated_at: at } : p));

  const handleDelete = async (part) => {
    if (!window.confirm(`Delete part ${part.part_number}? This cannot be undone.`)) return;
    setDeletingId(part.part_id);
    try {
      await api.delete(`/parts/${part.part_id}`);
      toast.success('Part deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete part');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelect = (partId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId);
      else next.add(partId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(p => p.part_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected part(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await api.delete('/parts/bulk', { data: { part_ids: [...selectedIds] } });
      toast.success(`Deleted ${selectedIds.size} part(s)`);
      setSelectedIds(new Set());
      fetchData();
    } catch {
      toast.error('Failed to delete parts');
    } finally {
      setBulkDeleting(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} className="inline ml-0.5" /> : <ChevronDown size={12} className="inline ml-0.5" />;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterCustomer) params.set('customer', filterCustomer);
      const token = localStorage.getItem('auth_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/parts/export?${params.toString()}`;
      const res = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : (filterCustomer ? `costing_${filterCustomer}.xlsx` : 'costing_review.zip');
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(filterCustomer ? `Exported ${filterCustomer}` : 'Exported per-customer files');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="parts-page">
      {/* Tab strip */}
      <div className="flex gap-1 border-b border-slate-200 mb-5">
        <button
          onClick={() => setTab('all-parts')}
          data-testid="tab-all-parts"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === 'all-parts'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-400 hover:text-slate-700'
          }`}
        >
          All Parts
        </button>
        {user?.role === 'admin' && (
          <button
            onClick={() => setTab('margin-compare')}
            data-testid="tab-margin-compare"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'margin-compare'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-400 hover:text-slate-700'
            }`}
          >
            Margin Compare
          </button>
        )}
      </div>

      {activeTab === 'margin-compare' && user?.role === 'admin' && <MarginCompareView />}

      {activeTab === 'all-parts' && (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {isCustomerView ? (
            <>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => navigate('/customers')} data-testid="back-to-customers"
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
                  <ArrowLeft size={14} /> Customers
                </button>
              </div>
              <h2 className="font-heading font-bold text-slate-900 text-xl">{customerFromUrl} — Parts</h2>
              <p className="text-slate-500 text-sm mt-0.5">{totalParts} part{totalParts !== 1 ? 's' : ''} for this customer</p>
            </>
          ) : isMarginRiskView ? (
            <>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => navigate('/')} data-testid="back-to-dashboard"
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
                  <ArrowLeft size={14} /> Dashboard
                </button>
              </div>
              <h2 className="font-heading font-bold text-red-600 text-xl flex items-center gap-2">
                <AlertTriangle size={20} /> Margin at Risk
              </h2>
              <p className="text-slate-500 text-sm mt-0.5">{totalParts} part{totalParts !== 1 ? 's' : ''} with margin below 10%</p>
            </>
          ) : (
            <>
              <h2 className="font-heading font-bold text-slate-900 text-xl">FG Parts Database</h2>
              <p className="text-slate-500 text-sm mt-0.5">{totalParts} parts · page {currentPage} of {totalPages}</p>
            </>
          )}
        </div>
        {(canEdit || user?.role === 'accounts_head' || user?.role === 'costing_editor') && (
          <div className="flex items-center gap-2">
            {canEdit && selectedIds.size > 0 && (
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                data-testid="bulk-delete-btn"
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50">
                <Trash2 size={15} /> Delete {selectedIds.size} Selected
              </button>
            )}
            {(user?.role === 'admin' || user?.role === 'accounts_head' || user?.role === 'costing_editor') && (
              <button onClick={() => setShowRMRevision(true)} data-testid="rm-revision-btn"
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-orange-600 transition-colors shadow-sm">
                RM Revision
              </button>
            )}
            {canEdit && (
              <button onClick={() => setShowAdd(true)} data-testid="add-part-btn"
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 transition-colors shadow-sm">
                <Plus size={15} /> Add Part
              </button>
            )}
          </div>
        )}
      </div>

      {/* RM Risk Summary */}
      {!loading && (filtered.length > 0 || summary) && (() => {
        // Prefer server-computed summary (full filtered set). Fall back to local compute
        // only if summary missing — e.g. for non-paginated callers.
        const behindCount = summary ? summary.rm_behind_count : filtered.filter(p => p.rm_variance?.status === 'behind').length;
        const safeCount   = summary ? summary.rm_safe_count   : filtered.filter(p => p.rm_variance?.status === 'safe').length;
        const noDataCount = summary ? summary.no_rm_data_count : filtered.filter(p => !p.rm_variance).length;
        if (behindCount === 0 && safeCount === 0 && noDataCount === 0) return null;
        return (
          <div className="grid grid-cols-3 gap-3" data-testid="rm-risk-summary">
            <button type="button" onClick={() => setFilterRmStatus(s => s === 'behind' ? '' : 'behind')}
              data-testid="kpi-filter-behind" title="Click to show only RM Behind parts"
              className={`text-left rounded-sm p-3 border transition-all cursor-pointer hover:shadow-sm ${behindCount > 0 ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-slate-50 border-slate-200'} ${filterRmStatus === 'behind' ? 'ring-2 ring-red-500 border-red-500' : ''}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold text-red-600 mb-0.5">RM Behind {filterRmStatus === 'behind' && <span className="text-red-500">· filtering ✓</span>}</p>
              <p className="font-mono font-black text-xl text-red-700" data-testid="kpi-rm-behind">{behindCount}</p>
              <p className="text-[10px] text-red-500">Market &gt; Locked Price</p>
            </button>
            <button type="button" onClick={() => setFilterRmStatus(s => s === 'safe' ? '' : 'safe')}
              data-testid="kpi-filter-safe" title="Click to show only RM Safe parts"
              className={`text-left bg-emerald-50 border border-emerald-200 rounded-sm p-3 transition-all cursor-pointer hover:shadow-sm hover:border-emerald-400 ${filterRmStatus === 'safe' ? 'ring-2 ring-emerald-500 border-emerald-500' : ''}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 mb-0.5">RM Safe {filterRmStatus === 'safe' && <span className="text-emerald-500">· filtering ✓</span>}</p>
              <p className="font-mono font-black text-xl text-emerald-700" data-testid="kpi-rm-safe">{safeCount}</p>
              <p className="text-[10px] text-emerald-500">Market &lt; Locked Price</p>
            </button>
            <button type="button" onClick={() => setFilterRmStatus(s => s === 'none' ? '' : 'none')}
              data-testid="kpi-filter-none" title="Click to show only parts with no market price"
              className={`text-left bg-slate-50 border border-slate-200 rounded-sm p-3 transition-all cursor-pointer hover:shadow-sm hover:border-slate-400 ${filterRmStatus === 'none' ? 'ring-2 ring-slate-500 border-slate-500' : ''}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">No RM Data {filterRmStatus === 'none' && <span>· filtering ✓</span>}</p>
              <p className="font-mono font-black text-xl text-slate-600" data-testid="kpi-no-rm-data">{noDataCount}</p>
              <p className="text-[10px] text-slate-400">No locked price set</p>
            </button>
          </div>
        );
      })()}

      {/* Customer RM Summary (only in customer view) */}
      {isCustomerView && !loading && (summary?.rm_grades_used?.length || filtered.length > 0) && (() => {
        // Use server-computed grades (full filtered set). Fall back to local aggregation
        // only if summary is missing.
        let grades;
        if (summary?.rm_grades_used) {
          grades = summary.rm_grades_used.map(g => ({
            grade: g.grade,
            category: g.category,
            lockedPrices: g.locked_prices || [],
            marketPrice: g.market_price || 0,
            parts: g.parts_count,
          }));
        } else {
          const rmGrades = {};
          filtered.forEach(p => {
            const grade = p.material_grade || 'Unknown';
            if (!rmGrades[grade]) {
              rmGrades[grade] = { grade, category: p.material_category, lockedPrices: new Set(), marketPrice: p.market_rm_price || 0, parts: 0 };
            }
            const price = p.rm_price_per_kg || p.rm_price || 0;
            if (price > 0) rmGrades[grade].lockedPrices.add(price);
            if (p.market_rm_price > 0) rmGrades[grade].marketPrice = p.market_rm_price;
            rmGrades[grade].parts++;
          });
          grades = Object.values(rmGrades).map(g => ({ ...g, lockedPrices: [...g.lockedPrices] }));
        }
        if (grades.length === 0) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="customer-rm-summary">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-3">
              RM Grades Used by {customerFromUrl}
              <span className="ml-2 text-slate-300 normal-case font-medium">· click a grade to filter</span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {grades.map(g => {
                const isActive = filterGrade === g.grade;
                // Colour the market price vs the locked price(s): market above locked = red
                // (cost has risen past what we locked), below = green, at par = blue.
                const lockedVals = g.lockedPrices || [];
                let marketCls = 'text-blue-600';
                if (g.marketPrice > 0 && lockedVals.length) {
                  const maxLocked = Math.max(...lockedVals);
                  const minLocked = Math.min(...lockedVals);
                  if (g.marketPrice > maxLocked) marketCls = 'text-red-600';
                  else if (g.marketPrice < minLocked) marketCls = 'text-emerald-600';
                }
                return (
                <button key={g.grade} type="button"
                  onClick={() => setFilterGrade(isActive ? '' : g.grade)}
                  data-testid={`grade-tile-${g.grade}`}
                  className={`text-left border rounded-sm px-3 py-2 transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-orange-50 border-orange-300 ring-1 ring-orange-300'
                      : 'bg-slate-50 border-slate-100 hover:border-slate-300 hover:bg-white'
                  }`}>
                  <p className={`font-mono font-bold text-sm ${isActive ? 'text-orange-700' : 'text-slate-800'}`}>{g.grade}</p>
                  <p className="text-[10px] text-slate-400">{g.category} &middot; {g.parts} part{g.parts !== 1 ? 's' : ''}</p>
                  <div className="mt-1 space-y-0.5">
                    {(g.lockedPrices || []).map(price => (
                      <span key={price} className="inline-block font-mono text-xs font-semibold text-slate-700 mr-2">
                        Locked: ₹{fmt(price)}/kg
                      </span>
                    ))}
                    {g.marketPrice > 0 && (
                      <p className={`font-mono text-[10px] font-semibold ${marketCls}`}>Market: ₹{fmt(g.marketPrice)}/kg</p>
                    )}
                    {(!g.lockedPrices || g.lockedPrices.length === 0) && <span className="text-[10px] text-slate-300">No price set</span>}
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2 flex-1 min-w-48">
          <Search size={14} className="text-slate-400 flex-shrink-0" />
          <input type="text" placeholder="Search part number, name, customer..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="parts-search"
            className="border-0 outline-none text-sm flex-1 bg-transparent placeholder:text-slate-400" />
        </div>
        <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}
          data-testid="filter-customer"
          className="border border-slate-300 rounded-sm h-8 px-2 text-sm bg-white focus:outline-none">
          <option value="">All Customers</option>
          {customers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          data-testid="filter-category"
          className="border border-slate-300 rounded-sm h-8 px-2 text-sm bg-white focus:outline-none">
          <option value="">All Materials</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {allRevisions.length > 1 && (
          <select value={filterRevision} onChange={e => setFilterRevision(e.target.value)}
            data-testid="filter-revision"
            className="border border-slate-300 rounded-sm h-8 px-2 text-sm bg-white focus:outline-none">
            <option value="">Active Revision</option>
            {allRevisions.map(r => <option key={r.label} value={r.label}>{r.label} ({r.part_count}){r.is_active ? ' *' : ''}</option>)}
          </select>
        )}
        <select value={filterRmStatus} onChange={e => setFilterRmStatus(e.target.value)}
          data-testid="filter-rm-status"
          className={`border rounded-sm h-8 px-2 text-sm bg-white focus:outline-none ${filterRmStatus === 'behind' ? 'border-red-400 text-red-700 font-semibold' : 'border-slate-300'}`}>
          <option value="">All RM Status</option>
          <option value="behind">⚠ RM Behind</option>
          <option value="safe">RM Safe</option>
          <option value="breakeven">RM Breakeven</option>
          <option value="none">No market price</option>
        </select>
        {(search || filterCustomer || filterCategory || filterGrade || filterRevision || filterRmStatus || isMarginRiskView) && (
          <button onClick={() => {
            setSearch(''); setFilterCustomer(''); setFilterCategory(''); setFilterGrade(''); setFilterRevision(''); setFilterRmStatus('');
            setCurrentPage(1);
            if (isCustomerView || isMarginRiskView) { navigate('/parts'); }
          }}
            className="text-slate-400 hover:text-slate-700 text-sm font-medium flex items-center gap-1">
            <X size={13} /> Clear{filterGrade ? ` · ${filterGrade}` : ''}
          </button>
        )}
        {/* Export buttons — role-gated */}
        {(user?.role === 'admin' || (user?.role === 'accounts_head' && filterCustomer)) && (
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-excel-btn"
            className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold border border-slate-300 rounded-sm bg-white hover:bg-slate-50 disabled:opacity-50 ml-auto"
          >
            {exporting ? (
              <span className="animate-spin h-3 w-3 border-b-2 border-slate-900 rounded-full"></span>
            ) : (
              <Download size={13} />
            )}
            {filterCustomer ? `Export ${filterCustomer}` : 'Export (.zip)'}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>
            Loading parts...
          </div>
        ) : (
          <div className="overflow-auto max-h-[70vh]">
            <table className="data-table w-full min-w-[1200px]">
              <thead>
                <tr>
                  {canEdit && (
                    <th className="w-10 text-center">
                      <input type="checkbox" data-testid="select-all-checkbox"
                        checked={filtered.length > 0 && selectedIds.size === filtered.length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer" />
                    </th>
                  )}
                  <th className="text-left">Part Number</th>
                  <th className="text-left">Part Name</th>
                  {!isCustomerView && <th className="text-left">Customer</th>}
                  <th className="text-left">Material</th>
                  <th className="text-right">Locked RM/kg</th>
                  <th className="text-right">Market RM/kg</th>
                  <th className="text-right cursor-pointer" onClick={() => toggleSort('rm_status')} title="Sort by RM status (Behind first)">
                    RM Status <SortIcon field="rm_status" />
                  </th>
                  <th className="text-right">Net RMC</th>
                  <th className="text-right">Process</th>
                  <th className="text-right">Plating</th>
                  <th className="text-right">BOP</th>
                  <th className="text-right">Add-on</th>
                  <th className="text-right cursor-pointer" onClick={() => toggleSort('selling_price')}>
                    Sell Price <SortIcon field="selling_price" />
                  </th>
                  <th className="text-right cursor-pointer" onClick={() => toggleSort('margin_pct')}>
                    Margin % <SortIcon field="margin_pct" />
                  </th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isCustomerView ? (canEdit ? 15 : 14) : (canEdit ? 16 : 15)}
                      className="py-12 text-center text-slate-400"
                    >
                      No parts found matching your search
                    </td>
                  </tr>
                ) : filtered.map(part => {
                  const locked = part.rm_price_per_kg || part.rm_variance?.locked_price || 0;
                  const market = part.market_rm_price || part.rm_variance?.market_price || 0;
                  const hasVariance = locked > 0 && market > 0 && locked !== market;
                  const sp = Number(part.selling_price) || 0;
                  const nrmc = netRmc(part);
                  const proc = procTotal(part);
                  const plat = Number(part.plating_cost) || 0;
                  const bop = bopTotal(part);
                  const add = addOn(part);
                  return (
                    <tr key={part.part_id} className={`group ${selectedIds.has(part.part_id) ? 'bg-slate-50' : ''}`}>
                      {canEdit && (
                        <td className="text-center">
                          <input type="checkbox" data-testid={`select-part-${part.part_id}`}
                            checked={selectedIds.has(part.part_id)}
                            onChange={() => toggleSelect(part.part_id)}
                            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer" />
                        </td>
                      )}
                      <td className="font-mono font-semibold text-slate-800">
                        <span className="inline-flex items-center gap-1">
                          {part.part_number}
                          {part.image_count > 0 && (
                            <span title={`${part.image_count} image/drawing${part.image_count > 1 ? 's' : ''}`}
                              data-testid={`part-image-count-${part.part_id}`}
                              className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-400">
                              <ImageIcon size={11} />{part.image_count}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="text-slate-700 max-w-[180px] truncate">{part.part_name}</td>
                      {!isCustomerView && <td className="text-slate-600 text-xs">{part.customer_name}</td>}
                      <td className="text-slate-600 text-xs">
                        {part.material_category}
                        <br/>
                        <span className="font-mono text-slate-400">
                          {part.material_grade}
                          {part.thickness_mm ? <span className="text-blue-400"> · {part.thickness_mm}mm</span> : null}
                        </span>
                      </td>
                      <td className="text-right">
                        <span className="font-mono text-sm font-semibold text-slate-800">₹{fmt(locked)}</span>
                        <span className="text-[10px] text-slate-400">/kg</span>
                        {(part.scrap_price_per_kg > 0 || part.scrap_price > 0) && (
                          <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                            Scrap: ₹{fmt(part.scrap_price_per_kg || part.scrap_price)}/kg
                          </p>
                        )}
                      </td>
                      <td className="text-right">
                        {market > 0 ? (
                          <>
                            <span className={`font-mono text-sm font-semibold ${hasVariance && market > locked ? 'text-red-600' : hasVariance && market < locked ? 'text-green-600' : 'text-slate-600'}`}>
                              ₹{fmt(market)}
                            </span>
                            <span className="text-[10px] text-slate-400">/kg</span>
                          </>
                        ) : <span className="text-xs text-slate-300">—</span>}
                        {part.market_scrap_price > 0 && (
                          <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                            Scrap: ₹{fmt(part.market_scrap_price)}/kg
                          </p>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <RmVarianceBadge variance={part.rm_variance} />
                          <PartNoteButton part={part} revealOnHover
                            attention={isNegativePart(part) || part.rm_variance?.status === 'behind'}
                            onSaved={(n, by, at) => applyNote(part.part_id, n, by, at)} />
                        </div>
                      </td>
                      <td className="text-right" data-testid={`net-rmc-${part.part_id}`}>
                        <CostCell value={nrmc} salePrice={sp} alwaysShow />
                      </td>
                      <td className="text-right" data-testid={`process-cost-${part.part_id}`}>
                        <CostCell value={proc} salePrice={sp} />
                      </td>
                      <td className="text-right" data-testid={`plating-cost-${part.part_id}`}>
                        <CostCell value={plat} salePrice={sp} emptyLabel="N/A" />
                        {platingMap && plat > 0 && (
                          <div className="mt-1 flex justify-end">
                            <PlatingSupplierBubble info={platingMap[part.part_id]} compact customerRate={plat} />
                          </div>
                        )}
                      </td>
                      <td className="text-right" data-testid={`bop-cost-${part.part_id}`}>
                        <CostCell value={bop} salePrice={sp} emptyLabel="N/A" />
                      </td>
                      <td className="text-right" data-testid={`addon-${part.part_id}`}>
                        <CostCell value={add} salePrice={sp} alwaysShow />
                      </td>
                      <td className="text-right font-mono text-sm font-semibold text-slate-800">{fmtCur(part.selling_price)}</td>
                      <td className="text-right"><MarginBadge pct={part.margin_pct} threshold={part.overhead_percentage || 15} /></td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {part.image_count > 0 && (
                            <button onClick={() => setViewImagesPart(part)} title="View Images & Drawings"
                              data-testid={`view-images-${part.part_id}`}
                              className="p-1.5 text-violet-500 hover:bg-violet-50 rounded-sm transition-colors">
                              <Eye size={13} />
                            </button>
                          )}
                          <button onClick={() => setCostingPart(part)} title="View Costing"
                            data-testid={`view-costing-${part.part_id}`}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-sm transition-colors">
                            <CheckCircle size={13} />
                          </button>
                          {part.has_revision_history && (
                            <button onClick={() => setRevHistoryPart(part)} title="Revision History"
                              data-testid={`view-rev-history-${part.part_id}`}
                              className="p-1.5 text-teal-500 hover:bg-teal-50 rounded-sm transition-colors">
                              <History size={13} />
                            </button>
                          )}
                          {part.history_count > 0 && (
                            <button onClick={() => setHistoryPart(part)} title="Costing History"
                              data-testid={`view-history-${part.part_id}`}
                              className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-sm transition-colors">
                              <Clock size={13} />
                            </button>
                          )}
                          {canEditParts && (
                            <button onClick={() => setEditPart(part)} title="Edit"
                              data-testid={`edit-part-${part.part_id}`}
                              className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm transition-colors">
                              <Edit size={13} />
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => handleDelete(part)} title="Delete"
                              disabled={deletingId === part.part_id}
                              data-testid={`delete-part-${part.part_id}`}
                              className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm transition-colors disabled:opacity-50">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-4 py-2.5" data-testid="pagination">
          <p className="text-xs text-slate-500">
            Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalParts)} of {totalParts} parts
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              data-testid="prev-page"
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={12} /> Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (currentPage <= 4) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = currentPage - 3 + i;
              }
              return (
                <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                  data-testid={`page-${pageNum}`}
                  className={`w-8 h-8 text-xs font-semibold rounded-sm transition-colors ${
                    currentPage === pageNum ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}>
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              data-testid="next-page"
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Next <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAdd && <PartModal materials={materials} customers={customers} onClose={() => setShowAdd(false)} onSaved={fetchData} />}
      {editPart && <PartModal part={editPart} materials={materials} customers={customers}
        onClose={() => setEditPart(null)} onSaved={fetchData}
        onGoToRMRevision={() => { setEditPart(null); setShowRMRevision(true); }} />}
      {costingPart && <CostBreakdownModal part={costingPart}
        platingInfo={platingMap ? platingMap[costingPart.part_id] : undefined}
        showPlatingSupplier={platingMap != null}
        onClose={() => setCostingPart(null)} />}
      {historyPart && <CostingHistoryModal part={historyPart} onClose={() => setHistoryPart(null)} />}
      {revHistoryPart && <RevisionHistoryModal part={revHistoryPart} onClose={() => setRevHistoryPart(null)} />}
      {viewImagesPart && <PartImagesViewerModal part={viewImagesPart} onClose={() => setViewImagesPart(null)} />}
      {showRMRevision && <RMRevisionPanel open={showRMRevision} defaultCustomer={customerFromUrl} onClose={(refresh) => { setShowRMRevision(false); if (refresh) fetchData(); }} />}
    </div>
      )}
    </div>
  );
}
